import { useCallback, useContext, useMemo } from 'react'

import { Tag } from 'antd'
import Cookie from 'js-cookie'
import { isArray } from 'lodash'
import { useSelector } from 'react-redux'
import { faGraduationCap } from '@fortawesome/pro-solid-svg-icons'

import { InstitutionGroupContext } from '../../../Providers/InstitutionGroupProvider'
import { GlobalFiltersContext } from '../../../Providers/GlobalFiltersProvider'
import { PRE_ACCEPTED, getState } from '../../../utils/entities/internship'
import { getTimezoneLessMoment } from '../../../utils/momentjs'
import { ACCEPTED, AUTO_ACCEPTED, EUROPEAN_DATE, OrderBy, PENDING, ROLE_INSTITUTION_GROUP_ADMIN, SCHEDULE_ADDED, SCHEDULE_TO_VALIDATE, SCHEDULE_VALIDATED } from '../../../utils/constants'
import { getAssistantBirthdate, getAssistantEmail, getAssistantFirstname, getAssistantLastname, getAssistantMatricule, getAssistantPhone, getInstitutionName, getSchoolName, getSpeciality } from '../../../utils/entities/maccsInternship'
import { PeriodExportParameters, getDocument, exportMaccsInternshipsByPeriod } from '../../../utils/api/export'
import { onError } from '../../../utils/apiHelper'
import { selectUser } from '../../../reducers/UserReducer'
import { getLanguageWithoutLocale } from '../../../utils/entities/user'
import { Action, TableActions } from '../../../Components/antd/Tables/DataTable/utils/actions'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import DataTable from '../../../Components/antd/Tables/DataTable/DataTable'
import SelectInstitutionFilter from '../../../Components/antd/Tables/DataTable/utils/SelectInstitutionFilter'

const InternshipsReportingTable = ({ data, loading, metadata, parameters, onParametersChange, onTriggerAction }) => {
  const institutionGroupContext = useContext(InstitutionGroupContext)
  const { selectedInstitution, setSelectedInstitution } = useContext(GlobalFiltersContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '240px' : '199px'})`, [Cookie.get('switch_user_id')])
  const columns = useMemo(() => {
    const rawColumns = [
      { title: t('State'), orderBy: OrderBy.STATE, key: d => getTagState(getState(d)) },
      { title: t('Start'), orderBy: OrderBy.START_DATE, key: d => getTimezoneLessMoment(d.startDate).format(EUROPEAN_DATE) },
      { title: t('End'), orderBy: OrderBy.END_DATE, key: d => getTimezoneLessMoment(d.endDate).format(EUROPEAN_DATE) }
    ]

    if (user.roles.includes(ROLE_INSTITUTION_GROUP_ADMIN)) {
      rawColumns.push({
        title: t('Institution'),
        orderBy: OrderBy.INSTITUTION,
        key: getInstitutionName,
        style: { maxWidth: '200px' },
        tooltip: { getter: getInstitutionName, placement: 'topLeft' }
      })
    }

    return rawColumns.concat([
      { title: t('Speciality'), orderBy: OrderBy.SECTOR, key: getSpeciality },
      { title: t('School'), orderBy: OrderBy.SCHOOL, key: getSchoolName },
      { title: t('Lastname'), orderBy: OrderBy.LASTNAME, key: getAssistantLastname },
      { title: t('Firstname'), orderBy: OrderBy.FIRSTNAME, key: getAssistantFirstname },
      { title: t('Email'), orderBy: OrderBy.EMAIL, key: getAssistantEmail },
      { title: t('Registration number'), orderBy: OrderBy.REGISTRATION, key: getAssistantMatricule },
      { title: t('Phone number'), orderBy: OrderBy.PHONE_NUMBER, key: getAssistantPhone },
      { title: t('Birthdate'), orderBy: OrderBy.BIRTHDATE, key: getAssistantBirthdate }
    ])
  }, [t, user])
  const actions = useMemo(() => [
    new Action({ title: 'Assistant sheet', icon: faGraduationCap, onClick: d => onTriggerAction(d, TableActions.DISPLAY_ASSISTANT_SHEET) })
  ], [onTriggerAction])
  const filters = useMemo(() => {
    const filters = []

    if (institutionGroupContext && isArray(institutionGroupContext.institutions) && institutionGroupContext.institutions.length > 0) {
      filters.push({
        key: 'institution',
        label: t('Institution'),
        component: SelectInstitutionFilter,
        props: { institutions: institutionGroupContext.institutions, value: selectedInstitution, onChange: setSelectedInstitution }
      })
    }

    return filters
  }, [t, SelectInstitutionFilter, institutionGroupContext?.institutions, selectedInstitution, setSelectedInstitution])
  const getTagState = state => {
    let stateTag = <Tag color='red'> {t('Invalid state')} </Tag>

    if (state === PENDING) {
      stateTag = <Tag color='geekblue'> {t('Pending')} </Tag>
    } else if (state === PRE_ACCEPTED) {
      stateTag = <Tag color='geekblue'> {t('Accepted')} </Tag>
    } else if (state === ACCEPTED) {
      stateTag = <Tag color='green'> {t('Accepted')} </Tag>
    } else if (state === AUTO_ACCEPTED) {
      stateTag = <Tag color='green'> {t('Accepted')} </Tag>
    } else if (state === SCHEDULE_ADDED) {
      stateTag = <Tag color='blue'> {t('Scheduled')} </Tag>
    } else if (state === SCHEDULE_VALIDATED) {
      stateTag = <Tag color='purple'> {t('Validated')} </Tag>
    } else if (state === SCHEDULE_TO_VALIDATE) {
      stateTag = <Tag color='orange'> {t('In waiting of validation')} </Tag>
    }

    return stateTag
  }
  const exportData = useCallback(format => {
    const periodParams = new PeriodExportParameters({ ...parameters.period, format })
    const params = periodParams.toBody()

    params.filters = parameters.filters
    params.startDate = params.start
    params.endDate = params.end
    params.language = getLanguageWithoutLocale(user)

    delete params.start
    delete params.end

    exportMaccsInternshipsByPeriod(params, user).then(json => {
      getDocument(
        json.job,
        user,
        () => onError(t('An error has occured during the exported file retrieving'))
      )
    }).catch(response => {
      onError(t('An error has occured during the export'))
    })
  }, [parameters, user])

  return (
    <div style={{ height: maxHeight }}>
      <DataTable
        actions={actions}
        columns={columns}
        data={data}
        display={{ period: true }}
        filters={filters}
        loading={loading}
        metadata={metadata}
        parameters={parameters}
        onExport={exportData}
        onParametersChange={onParametersChange}
      />
    </div>
  )
}

export default InternshipsReportingTable
